import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Configuración por empresa: cobro automático de ventas al contado. */
export default function CobroContadoCard({ token, logoTipo, puedeEditar }) {
  const [cfg, setCfg] = useState({ cobro_automatico_contado: false, cuenta_cobro_automatico_id: null });
  const [cuentas, setCuentas] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const q = logoTipo ? `?logo_tipo=${encodeURIComponent(logoTipo)}` : "";

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/admin/config-ventas-cobro${q}`, { headers }).then(r => r.ok ? r.json() : null).then(c => c && setCfg(c)).catch(() => {});
    fetch(`${API}/admin/cuentas-bancarias${q}`, { headers }).then(r => r.ok ? r.json() : []).then(l => setCuentas(Array.isArray(l) ? l : [])).catch(() => {});
  }, [token, logoTipo]); // eslint-disable-line

  const guardar = async (cambios) => {
    if (!puedeEditar) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/config-ventas-cobro`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ logo_tipo: logoTipo || "", ...cambios }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      setCfg(await res.json());
      toast.success("Configuración guardada");
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSaving(false);
  };

  const opciones = [
    { v: false, t: "Cobrar después", d: "Comportamiento actual: la venta al contado queda pendiente y se cobra con el botón Pagar." },
    { v: true, t: "Cobrar al registrar", d: "Al registrar una venta al contado se cobra en el mismo momento: queda pagada e ingresa a la cuenta." },
  ];

  return (
    <div className="border border-white/10 rounded-xl p-5 bg-white/3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
            <Banknote className="w-4 h-4 text-emerald-300" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">Cobro de ventas al contado</p>
            <p className="text-slate-400 text-xs mt-0.5 max-w-xl">
              Definí si las ventas al contado de esta empresa se cobran solas al registrarlas o si hay que darle Pagar después. Aplica a Facturas y Boletas.
            </p>
          </div>
        </div>
        {saving && <Loader2 className="w-4 h-4 text-emerald-300 animate-spin" />}
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        {opciones.map(op => {
          const activa = !!cfg.cobro_automatico_contado === op.v;
          return (
            <button key={String(op.v)} disabled={saving || !puedeEditar}
              onClick={() => guardar({ cobro_automatico_contado: op.v })}
              className={`text-left rounded-xl border px-4 py-3 transition-all ${activa ? "bg-emerald-500/15 border-emerald-400/50 text-emerald-100" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"} disabled:opacity-60`}>
              <span className="block text-sm font-semibold">{op.t}</span>
              <span className="block text-xs mt-1 text-slate-400">{op.d}</span>
            </button>
          );
        })}
      </div>
      {cfg.cobro_automatico_contado && (
        <div className="mt-3">
          <label className="text-slate-400 text-xs mb-1 block">Cuenta donde ingresa el cobro</label>
          <select disabled={saving || !puedeEditar}
            value={cfg.cuenta_cobro_automatico_id || ""}
            onChange={e => guardar({ cuenta_cobro_automatico_id: e.target.value || null })}
            className="w-full md:w-96 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
            <option value="">Cuenta predeterminada de la moneda</option>
            <option value="__elegir__">A elección: preguntar la cuenta al registrar la venta</option>
            {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda})</option>)}
          </select>
          <p className="text-slate-500 text-[11px] mt-1">Con "A elección", al guardar la factura o boleta se abre una ventana para elegir la caja/banco donde ingresa el cobro. Si la cuenta fija no coincide con la moneda de la venta, se usa la predeterminada de esa moneda. Si no hay ninguna, la venta queda pendiente.</p>
        </div>
      )}
    </div>
  );
}
